import { useState, useEffect, useCallback, useRef } from "react";
import { ErrorStackDataType } from "@preveil-api";
import { FSMessage, FSStatus } from "src/common/keys/protos/collections_pb";
import {
  Account, useAppSelector, useAppDispatch, FullNodePath, PathNode, useGetGrants, PathInfo, PathList, PermissionSetType, GrantSetType, useGetPermissions,
  MaintainerGrantsMap, PermissionSetTypeMap, DriveErrorMessages, getEnumKey, MessageHandlerDisplayType, ErrorStackItem, Message, DriveConstants, DefaultRoutes,
  DriveConstantsType
} from "src/common";
import { uiActions } from "src/store";
import { RootState } from "src/store/configureStore";


export function useGetPathList(current_account: Account) {
  const [breadcrumbs_paths, setBreadcrumbPaths] = useState<PathInfo[] | undefined>();
  const root_info = useAppSelector((state: RootState) => state.drive.root_info); // TEMP until  BACK-1318 gets sorted
  const default_permissions = useAppSelector((state: RootState) => state.drive.default_permissions);
  const { permissions, getPermissions, destroyPermissions } = useGetPermissions(current_account);
  const default_grants = useAppSelector((state: RootState) => state.drive.default_grants);
  const { getGrants, destroyGrants, grants, error: grant_error } = useGetGrants(current_account);
  const dispatch = useAppDispatch();

  // const dispatch = useAppDispatch();
  const path_list_ref = useRef<PathNode[]>();
  // Description: hold working nodes
  const nodes_ref = useRef<PathNode[]>([]);
  const working_node_ref = useRef<PathNode>();
  const node_queue_ref = useRef<PathInfo[]>([]);

  const permission_sets_ref = useRef<PermissionSetTypeMap>(new Map<string, PermissionSetType>);
  function setPermissionSetsRef(id: string, permission_set: PermissionSetType) {
    permission_sets_ref.current.set(id, permission_set);
  }
  function getPermissionSetRef(collection_id: string): PermissionSetType | undefined {
    return permission_sets_ref.current.get(collection_id);
  }

  const maintainer_grants_ref = useRef<MaintainerGrantsMap>(new Map<string, GrantSetType>);
  function setMaintainerGrantsRef(id: string, maintainer_grants: GrantSetType) {
    maintainer_grants_ref.current.set(id, maintainer_grants);
  }

  // Description: Handle the name field in the watch node list
  useEffect(() => {
    if (!!permissions && !!working_node_ref.current) {
      setPermissionSetsRef(permissions.collection_id.B64(), permissions);
      destroyPermissions(); // NOTE: Return the permission state to undefined to be able to detect future changes
      handlePathNodePermissions(working_node_ref.current, permissions);
    }
  }, [permissions]);

  // Description: trigger breadcrumb path node decrypt on grant fetch
  useEffect(() => {
    if (!!grants) {
      setMaintainerGrantsRef(grants.id, grants);
      if (!!working_node_ref.current) {
        const permissions_ref = getPermissionSetRef(working_node_ref.current?.collection_id);
        !!permissions_ref ? handlePathNodeGrants(working_node_ref.current, permissions_ref, grants) :
          handleItemError(DriveErrorMessages.default, { stack_error: { data: working_node_ref.current }, stack_message: DriveErrorMessages.error_sending_request });
      } else {
        handleItemError(DriveErrorMessages.default, { stack_error: { data: `missing working_node_ref on grant fetch: Collection_id: ${grants.collection_id}` }, stack_message: DriveErrorMessages.error_sending_request });
      };
    }

    !!grant_error &&
      handleItemError(DriveErrorMessages.default, { stack_error: { data: working_node_ref.current }, stack_message: DriveErrorMessages.error_sending_request });
  }, [grants, grant_error]);

  // Description: Handle the name field in the path node list
  async function handlePathNodePermissions(node: PathNode, _permissions: PermissionSetType) {
    const maintainer_id = node.maintainer_id;
    if (!!maintainer_id) {
      // NOTE: Do a check for cached assets to bypass getting same collection again
      const _maintainer_grants = maintainer_grants_ref.current.get(maintainer_id);
      !!_maintainer_grants ? handlePathNodeGrants(node, _permissions, _maintainer_grants) :
        getGrants(maintainer_id, node.collection_id, default_permissions, default_grants);
    } else {
      handleDecryptName(node, _permissions);
    }
  }

  // Description: Handle the name field in the path node list
  async function handlePathNodeGrants(node: PathNode, _permissions: PermissionSetType, _grants: GrantSetType) {
    handleDecryptName(node, _permissions, _grants);
    destroyGrants();
  }

  // Description:  Handle entry name for all types and move to next item
  // NOTE: Handle the parent DirectoryInfo (symm key)
  async function handleDecryptName(node: PathNode, _permissions: PermissionSetType, _grants?: GrantSetType) {
    const is_root = !!root_info && root_info.id === node.id;
    const path_list = is_root ? {
      path: DriveConstants.ROOT_DIRECTORY,
      url: DefaultRoutes.drive_root,
      id: node.id
    } : await PathList.buildPathInfoFromPathNode(node, current_account, _permissions, _grants);
    node_queue_ref.current.push(path_list);
    destroyGrants(); // NOTE: destroy grants to allow for a next iteration
    getNext();
  }

  // Description: Move to next item in queue
  function getNext() {
    working_node_ref.current = nodes_ref.current.shift();
    if (!!working_node_ref.current) {
      const _permissions = getPermissionSetRef(working_node_ref.current.collection_id);
      !!_permissions ? handlePathNodePermissions(working_node_ref.current, _permissions) :
        getPermissions(working_node_ref.current.collection_id, default_permissions);
    } else {
      handleSuccess();
    }
  }

  // Description: Return all breadcrumbs
  function handleSuccess() {
    setBreadcrumbPaths(node_queue_ref.current);
  }

  // Description:  Handle queue item error
  function handleItemError(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null) {
    const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
    const stack = new ErrorStackItem("[useFetchWatched Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
    dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), stack));
    getNext();
  }

  // Description: reset hook 
  function reset() {
    nodes_ref.current = [];
    node_queue_ref.current = [];
    working_node_ref.current = undefined;
    path_list_ref.current = undefined;
    destroyGrants();
    destroyPermissions();
  }

  const buildBreadcrumbs = useCallback((
    collection_id: string,
    full_path_list: FullNodePath,
    drive_module: DriveConstantsType = DriveConstants.DRIVE,
    last_node_is_link: boolean = false) => {
    reset();
    path_list_ref.current = PathList.initFullNodePath(collection_id, full_path_list, root_info, drive_module, last_node_is_link);
    nodes_ref.current = path_list_ref.current.slice();
    getNext();
  }, []);

  return {
    buildBreadcrumbs,
    breadcrumbs_paths
  };
}
